import { Box, Theme, useTheme } from '@mui/material';
import * as React from 'react';
import SwipeableViews from 'react-swipeable-views';
import { makeStyles } from 'tss-react/mui';
import useWidth from '../Hooks/useWidth';
import TeceoSliderLib, { NavigationDirection } from './TeceoSlickerLib';
import TeceoSlickerNavigators from './TeceoSlickerNavigators';

const useStyles = makeStyles()((theme: Theme) => ({
  swipeableContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  box: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  item: {
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      cursor: 'zoom-out',
      justifyContent: 'center',
      width: '100%',
      position: 'absolute',
    },
  },
}));

const SWIPEABLE_FULL_HEIGHT_STYLE = { height: '100%' };

type TeceoSlickerProps<T> = {
  items: T[];
  selectedIndex: number;
  selectCursor?: string;
  keyAcessor: keyof T;
  onChangeSelectedIndex: (indexToSelect: number) => void;
  renderItem: (item: T, index: number, isSelected: boolean) => React.ReactNode;
  onItemClick?: (item: T, event: React.MouseEvent<HTMLDivElement>) => void;
};
const TeceoSlicker = <T,>({
  items,
  renderItem,
  keyAcessor,
  onItemClick,
  selectCursor,
  selectedIndex,
  onChangeSelectedIndex,
}: TeceoSlickerProps<T>) => {
  const { classes } = useStyles();
  const { mobileWidth } = useWidth();
  const renderSwipeable = mobileWidth;
  const theme = useTheme();

  const selectedItem = items[selectedIndex];

  const navigate = (direction: NavigationDirection) => {
    onChangeSelectedIndex(TeceoSliderLib.getNextIndex(selectedIndex, direction, items.length));
  };

  const handleItemClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onItemClick && selectedItem) {
      onItemClick(selectedItem, event);
    }
  };

  return (
    <Box className={classes.box}>
      {renderSwipeable ? (
        <SwipeableViews
          axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={selectedIndex}
          onChangeIndex={onChangeSelectedIndex}
          enableMouseEvents
          className={classes.swipeableContainer}
          containerStyle={SWIPEABLE_FULL_HEIGHT_STYLE}
          slideStyle={SWIPEABLE_FULL_HEIGHT_STYLE}
          resistance
        >
          {items.map((item: T, index: number) => (
            <div key={`${item[keyAcessor]}`} className={classes.item} onClick={handleItemClick}>
              {renderItem(item, index, index === selectedIndex)}
            </div>
          ))}
        </SwipeableViews>
      ) : (
        <>
          {selectedItem && (
            <div key={`${selectedItem[keyAcessor]}`} className={classes.item}>
              {renderItem(selectedItem, selectedIndex, true)}
            </div>
          )}

          <TeceoSlickerNavigators
            navigate={navigate}
            canNavigate={items.length > 1}
            onClick={handleItemClick}
            selectCursor={selectCursor}
          />
        </>
      )}
    </Box>
  );
};

export default TeceoSlicker;
